<template>
  <div
    ref="containerRef"
    class="dropdown-trigger"
  >
    <slot
      :aria-controls="menuId"
      :aria-expanded="isOpen"
      name="trigger"
      :open="isOpen"
      :toggle="toggleMenu"
    />
  </div>

  <Transition name="dropdown">
    <div
      v-if="isOpen"
      :id="menuId"
      ref="menuRef"
      class="dropdown-menu"
      :class="{ [position || 'bottom-right']: true, 'no-notch': noNotch }"
      :style="menuStyle"
      tabindex="-1"
    >
      <slot
        :close="closeMenu"
        name="content"
      />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { type ComponentPublicInstance, nextTick, onUnmounted, ref, type VNode, watch } from 'vue';

import { useId } from '../../../composables';

/**
 * Dropdown menu with configurable positioning
 *
 * @example Basic usage
 * ```vue
 * <DropdownMenu>
 *   <template #trigger="{ toggle }">
 *     <BaseButton @click="toggle">Menu</BaseButton>
 *   </template>
 *   <template #content>
 *     <div>Menu content</div>
 *   </template>
 * </DropdownMenu>
 * ```
 */
export interface DropdownMenuProps {
  /**
   * Menu positioning relative to trigger
   * @default 'bottom-right'
   */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

  /**
   * Hide the decorative notch/arrow
   * @default false
   */
  noNotch?: boolean;

  /**
   * Alternative element to position relative to
   * Useful when trigger contains multiple elements
   * @default undefined (uses trigger's first child)
   */
  positionRelativeTo?: HTMLElement | ComponentPublicInstance | null;

  /**
   * Vertical offset from trigger (CSS value)
   * Added for bottom positions, subtracted for top positions
   * Can also be set via --dropdown-vertical-offset CSS variable
   * @default '0px'
   */
  verticalOffset?: string;

  /**
   * Horizontal offset from trigger (CSS value)
   * Subtracted for right positions, added for left positions
   * Can also be set via --dropdown-horizontal-offset CSS variable
   * @default '0px'
   */
  horizontalOffset?: string;
}

interface TriggerSlotProps {
  toggle: () => void;
  open: boolean;
  /**
   * The panel is a generic container, not a menu, listbox, tree, grid or dialog,
   * so there is no honest `aria-haspopup` value for it — ARIA maps `true` to
   * `menu`. Expanded state plus the controlled id is the disclosure pattern.
   */
  ariaExpanded: boolean;
  ariaControls: string;
}

interface ContentSlotProps {
  close: () => void;
}

const props = defineProps<DropdownMenuProps>();

defineSlots<{
  trigger(props: TriggerSlotProps): VNode;
  content(props: ContentSlotProps): VNode;
}>();

const isOpen = ref(false);
const containerRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const menuId = useId('dropdown-menu');

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

const openMenu = () => {
  isOpen.value = true;
};

const closeMenu = () => {
  isOpen.value = false;
};

defineExpose({
  open: openMenu,
  close: closeMenu,
});

const handleClickOutside = (event: MouseEvent) => {
  if (!isOpen.value) return;

  const container = containerRef.value;
  const menu = menuRef.value;

  // Use composedPath() to properly detect clicks inside Shadow DOM
  // event.target gets retargeted when crossing shadow boundaries
  const path = event.composedPath();

  // Check if any element in the event path is inside our container or menu
  const clickedInside = path.some(element => {
    if (!(element instanceof Node)) return false;
    return container?.contains(element) || menu?.contains(element);
  });

  if (clickedInside) return;

  closeMenu();
};

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) {
    closeMenu();
  }
};

const menuStyle = ref<Record<string, string>>({});
const rafId = ref<number | null>(null);

const schedulePositionUpdate = () => {
  if (!rafId.value) {
    rafId.value = requestAnimationFrame(() => {
      calculatePosition();
      rafId.value = null;
    });
  }
};

const handleScroll = () => {
  if (isOpen.value) {
    schedulePositionUpdate();
  }
};

const handleResize = () => {
  if (isOpen.value) {
    schedulePositionUpdate();
  }
};

const addPositionEventListeners = () => {
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleResize, { passive: true });
};

const removePositionEventListeners = () => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', handleResize);
};

onUnmounted(() => {
  /**
   * Remove all listeners unconditionally to prevent race conditions
   * Safe to call even if listeners weren't attached
   */
  document.removeEventListener('click', handleClickOutside, true);
  document.removeEventListener('keydown', handleEscape);
  removePositionEventListeners();

  if (rafId.value) {
    cancelAnimationFrame(rafId.value);
    rafId.value = null;
  }
});

const calculatePosition = () => {
  if (!menuRef.value) return;

  /**
   * Determine trigger element for positioning
   * positionRelativeTo can be ComponentPublicInstance (access via $el) or unwrapped HTMLElement
   * Falls back to first child of container if not provided
   */
  let triggerElement: HTMLElement | null = null;

  if (props.positionRelativeTo) {
    if ('$el' in props.positionRelativeTo) {
      triggerElement = props.positionRelativeTo.$el as HTMLElement;
    } else {
      triggerElement = props.positionRelativeTo as HTMLElement;
    }
  }

  const trigger = triggerElement || (containerRef.value?.querySelector(':first-child') as HTMLElement);
  if (!trigger) return;

  const triggerRect = trigger.getBoundingClientRect();

  // Use offsetWidth/offsetHeight which reliably includes padding and borders
  const menuWidth = menuRef.value.offsetWidth;
  const menuHeight = menuRef.value.offsetHeight;

  let calculatedTop = 0;
  let calculatedLeft = 0;

  switch (props.position || 'bottom-right') {
    case 'bottom-right':
      calculatedTop = triggerRect.bottom;
      calculatedLeft = triggerRect.right - menuWidth;
      break;
    case 'bottom-left':
      calculatedTop = triggerRect.bottom;
      calculatedLeft = triggerRect.left;
      break;
    case 'top-right':
      calculatedTop = triggerRect.top - menuHeight;
      calculatedLeft = triggerRect.right - menuWidth;
      break;
    case 'top-left':
      calculatedTop = triggerRect.top - menuHeight;
      calculatedLeft = triggerRect.left;
      break;
  }

  menuStyle.value = {
    '--calculated-top': `${calculatedTop}px`,
    '--calculated-left': `${calculatedLeft}px`,
    ...(props.verticalOffset && { '--dropdown-vertical-offset': props.verticalOffset }),
    ...(props.horizontalOffset && { '--dropdown-horizontal-offset': props.horizontalOffset }),
  };
};

watch(
  () => isOpen.value,
  async open => {
    if (open) {
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
      addPositionEventListeners();

      await nextTick();
      // Double rAF: first frame lets the browser insert the element,
      // second frame guarantees offsetWidth is computed before measuring
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          calculatePosition();
        });
      });
    } else {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
      removePositionEventListeners();
    }
  },
);
</script>

<style>
.dropdown-trigger {
  position: relative;
  display: contents;
}

.dropdown-menu {
  position: fixed;
  background-color: var(--dropdown-background);
  border-radius: var(--border-radius-md);
  border: var(--size-border-thin) solid var(--dropdown-border);
  box-shadow: var(--box-shadow-down);
  min-width: var(--dropdown-min-width);
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: var(--gap-xs);
  padding: var(--dropdown-padding);

  &.bottom-right {
    top: calc(var(--calculated-top) + var(--dropdown-vertical-offset, 0px));
    left: calc(var(--calculated-left) - var(--dropdown-horizontal-offset, 0px));

    &:before {
      top: var(--dropdown-arrow-offset);
      right: var(--dropdown-arrow);
    }
  }

  &.bottom-left {
    top: calc(var(--calculated-top) + var(--dropdown-vertical-offset, 0px));
    left: calc(var(--calculated-left) + var(--dropdown-horizontal-offset, 0px));

    &:before {
      top: var(--dropdown-arrow-offset);
      left: var(--dropdown-arrow);
    }
  }

  &.top-right {
    top: calc(var(--calculated-top) - var(--dropdown-vertical-offset, 0px));
    left: calc(var(--calculated-left) - var(--dropdown-horizontal-offset, 0px));

    &:before {
      bottom: var(--dropdown-arrow-offset);
      right: var(--dropdown-arrow);
    }
  }

  &.top-left {
    top: calc(var(--calculated-top) - var(--dropdown-vertical-offset, 0px));
    left: calc(var(--calculated-left) + var(--dropdown-horizontal-offset, 0px));

    &:before {
      bottom: var(--dropdown-arrow-offset);
      left: var(--dropdown-arrow);
    }
  }

  &:not(.no-notch)::before {
    background-image: linear-gradient(to bottom right, var(--color-light) calc(50% + 1px), transparent calc(50% + 1px));
    border: var(--size-border-thin) solid var(--dropdown-border);
    border-width: var(--size-border-thin) 0 0 var(--size-border-thin);
    content: '';
    position: absolute;
    height: var(--dropdown-arrow);
    width: var(--dropdown-arrow);
    transform: rotate(45deg);
  }
}

/* Dropdown fade + scale transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition:
    opacity var(--transition-duration) var(--transition-ease),
    transform var(--transition-duration) var(--transition-ease);
  will-change: opacity, transform;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
